// src/auth/RequireManagerOrAdmin.jsx
import { useEffect, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";

import { useAuth } from "./AuthProvider";
import AppLoadingOverlay from "../components/ui/AppLoadingOverlay";

export default function RequireManagerOrAdmin({ children }) {
  const {
    user,
    authLoading,
    role,
    blockedMessage,
    blockedReason,
    mustAcceptCurrentDisclaimer,
  } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  const handledRef = useRef(false);

  const isAllowed = role === "admin" || role === "manager";

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      if (handledRef.current) return;
      handledRef.current = true;
      nav("/login", { replace: true, state: { from: loc } });
      return;
    }

    if (blockedReason) {
      if (handledRef.current) return;
      handledRef.current = true;

      (async () => {
        await signOut(auth).catch(() => {});
        nav("/login", {
          replace: true,
          state: {
            authError: blockedMessage || "Access blocked.",
            from: loc,
            toastId: Date.now(),
          },
        });
      })();
      return;
    }

    if (mustAcceptCurrentDisclaimer) {
      if (handledRef.current) return;
      handledRef.current = true;
      nav("/accept-disclaimer", { replace: true, state: { from: loc } });
    }
  }, [authLoading, user, blockedReason, blockedMessage, mustAcceptCurrentDisclaimer, nav, loc]);

  if (authLoading || blockedReason) {
    return (
      <AppLoadingOverlay
        title="Checking access…"
        subtitle="Verifying permissions"
        visible
      />
    );
  }

  if (!user) return null;

  if (mustAcceptCurrentDisclaimer) return null;

  if (user && !isAllowed) {
    return <Navigate to="/clients" replace />;
  }

  return children;
}
